import { useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'

import { axiosInstance } from '../helpers'

export interface VendorPerformanceProperty {
    id: number
    name: string
}

export interface VendorPerformanceAnalytics {
    vendor_id: number
    vendor_name: string
    in_house: boolean
    properties: VendorPerformanceProperty[]
    start_date: string | null
    end_date: string | null
    total_workorders: number
    cai_count: number
    cai_rate: number
    vendor_transitioned_count: number
    vendor_transitioned_total: number
    vendor_transitioned_wo_rate: number
    total_projects: number
    total_forecasted: number
    total_invoiced: number
    total_change_orders: number
    avg_change_orders_per_project: number
    early_workorder_count: number
    early_workorder_rate: number
    go_back_count: number
    go_back_rate: number
}

interface UseVendorPerformanceParams {
    vendorId: number | null
    organizationId?: number | null
    startDate?: string
    endDate?: string
    enabled?: boolean
}

interface UseVendorPerformanceListParams {
    organizationId?: number | null
    startDate?: string
    endDate?: string
    enabled?: boolean
}

export const useVendorPerformance = ({
    vendorId,
    organizationId,
    startDate,
    endDate,
    enabled = true,
}: UseVendorPerformanceParams) => {
    const hasValidDateRange = !startDate || !endDate || startDate <= endDate

    const { data, isLoading, isFetching, isError, error } = useQuery<
        AxiosResponse<VendorPerformanceAnalytics>,
        Error
    >({
        queryKey: [
            'vendor-performance',
            vendorId,
            organizationId,
            startDate,
            endDate,
        ],
        queryFn: () =>
            axiosInstance.get('stats/vendor-performance/', {
                params: {
                    vendor: vendorId,
                    ...(organizationId != null && {
                        organization_id: organizationId,
                    }),
                    ...(startDate && { start_date: startDate }),
                    ...(endDate && { end_date: endDate }),
                },
            }),
        enabled: !!vendorId && enabled && hasValidDateRange,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    return {
        analytics: data?.data ?? null,
        isLoading: isLoading || isFetching,
        isError,
        error,
    }
}

export const useVendorPerformanceList = ({
    organizationId,
    startDate,
    endDate,
    enabled = true,
}: UseVendorPerformanceListParams) => {
    const hasValidDateRange = !startDate || !endDate || startDate <= endDate

    const { data, isLoading, isFetching, isError, error } = useQuery<
        AxiosResponse<VendorPerformanceAnalytics[]>,
        Error
    >({
        queryKey: [
            'vendor-performance-list',
            organizationId,
            startDate,
            endDate,
        ],
        queryFn: () =>
            axiosInstance.get('stats/org-vendor-performance/', {
                params: {
                    ...(organizationId != null && {
                        organization_id: organizationId,
                    }),
                    ...(startDate && { start_date: startDate }),
                    ...(endDate && { end_date: endDate }),
                },
            }),
        enabled: !!organizationId && enabled && hasValidDateRange,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    return {
        rows: data?.data ?? [],
        isLoading: isLoading || isFetching,
        isError,
        error,
    }
}
